import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WhatsAppLinkPanel } from "./WhatsAppLinkPanel";
import { getApiBase } from "../apiBase";

const API_BASE = getApiBase();
const GOOGLE_MAPS_API_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
const GOOGLE_MAPS_SCRIPT_ID = "afterme-google-maps-places";
let googleMapsPlacesPromise = null;

const RELEASE_STATUS_COPY = {
  disabled: {
    label: "Monitoring off",
    detail: "Quiet inactivity confirmation is disabled for now.",
    tone: "muted",
  },
  active: {
    label: "Monitoring active",
    detail: "No action is needed unless your quiet inactivity window passes.",
    tone: "good",
  },
  check_sent: {
    label: "Quiet check-in sent",
    detail: "A private email check-in was sent to you first.",
    tone: "attention",
  },
  reminder_sent: {
    label: "Reminder sent",
    detail: "A follow-up email reminder has been sent after no activity.",
    tone: "attention",
  },
  trusted_contact_pinged: {
    label: "Trusted contact follow-up",
    detail: "A trusted contact has been asked to quietly check in.",
    tone: "attention",
  },
  manual_review: {
    label: "Manual review stage",
    detail: "The workflow should now be reviewed carefully before anything proceeds.",
    tone: "warning",
  },
};

const EMPTY_FORM = {
  name: "",
  preferredName: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  region: "",
  postalCode: "",
  country: "Canada",
  occupation: "",
  employer: "",
  dateOfBirth: "",
  bloodType: "",
  allergies: "",
  healthSummary: "",
  medicationsNotes: "",
  emergencyContactName: "",
  emergencyContactPhone: "",
};

const PROFILE_STEPS = [
  {
    id: "basics",
    shortLabel: "Basics",
    title: "Personal information",
    detail: "Name, email, and the essentials tied to your account.",
  },
  {
    id: "contact",
    shortLabel: "Contact",
    title: "Contact and address",
    detail: "Phone, WhatsApp, and where trusted people can reach you.",
  },
  {
    id: "context",
    shortLabel: "Context",
    title: "Work and identity context",
    detail: "Optional background that can help future verification.",
  },
  {
    id: "health",
    shortLabel: "Health",
    title: "Health and emergency context",
    detail: "Private notes that stay with your legacy file.",
  },
  {
    id: "release",
    shortLabel: "Release",
    title: "Release confidence",
    detail: "Choose how quiet inactivity checks should progress.",
  },
];

function computeAgeFromIso(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return null;
  }
  const [y, m, d] = iso.split("-").map(Number);
  const today = new Date();
  let age = today.getFullYear() - y;
  const hadBirthday =
    today.getMonth() + 1 > m || (today.getMonth() + 1 === m && today.getDate() >= d);
  if (!hadBirthday) {
    age -= 1;
  }
  if (!Number.isFinite(age) || age < 0 || age > 130) {
    return null;
  }
  return age;
}

function formatSubscriptionDate(iso, locale = "en") {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function profileToForm(profile, authUser) {
  const p = profile || {};
  return {
    ...EMPTY_FORM,
    name: p.name ?? authUser?.name ?? "",
    preferredName: p.preferredName ?? "",
    phone: p.phone ?? "",
    addressLine1: p.addressLine1 ?? "",
    addressLine2: p.addressLine2 ?? "",
    city: p.city ?? "",
    region: p.region ?? "",
    postalCode: p.postalCode ?? "",
    country: p.country || "Canada",
    occupation: p.occupation ?? "",
    employer: p.employer ?? "",
    dateOfBirth: p.dateOfBirth ?? "",
    bloodType: p.bloodType ?? "",
    allergies: p.allergies ?? "",
    healthSummary: p.healthSummary ?? "",
    medicationsNotes: p.medicationsNotes ?? "",
    emergencyContactName: p.emergencyContactName ?? "",
    emergencyContactPhone: p.emergencyContactPhone ?? "",
  };
}

function formatDateTimeLabel(value, locale = "en") {
  if (!value) {
    return "Not yet";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Not yet";
  }
  return new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function addMinutesToIso(value, minutes) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  date.setMinutes(date.getMinutes() + Number(minutes || 0));
  return date.toISOString();
}

function buildTrustedContactLabel(option) {
  const parts = [option?.name || "Family member"];
  if (option?.relation) {
    parts.push(option.relation);
  }
  if (option?.email) {
    parts.push(option.email);
  }
  return parts.join(" · ");
}

function loadGoogleMapsPlacesApi(apiKey) {
  if (!apiKey || typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve(null);
  }
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google.maps);
  }
  if (googleMapsPlacesPromise) {
    return googleMapsPlacesPromise;
  }

  googleMapsPlacesPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
    const handleLoad = () => {
      if (window.google?.maps?.places) {
        resolve(window.google.maps);
      } else {
        googleMapsPlacesPromise = null;
        reject(new Error("Google Maps Places did not load correctly."));
      }
    };
    const handleError = () => {
      googleMapsPlacesPromise = null;
      reject(new Error("Google Maps Places failed to load."));
    };

    if (existing) {
      if (window.google?.maps?.places) {
        resolve(window.google.maps);
        return;
      }
      existing.addEventListener("load", handleLoad, { once: true });
      existing.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.head.appendChild(script);
  });

  return googleMapsPlacesPromise;
}

function readAddressComponent(components, type, valueKey = "long_name") {
  const match = (components || []).find((component) => component.types?.includes(type));
  return match ? match[valueKey] || "" : "";
}

function parseAutocompleteAddress(place) {
  const components = place?.address_components || [];
  const streetNumber = readAddressComponent(components, "street_number");
  const route = readAddressComponent(components, "route");
  const locality =
    readAddressComponent(components, "locality") ||
    readAddressComponent(components, "postal_town") ||
    readAddressComponent(components, "sublocality_level_1") ||
    readAddressComponent(components, "administrative_area_level_3");
  const region = readAddressComponent(components, "administrative_area_level_1");
  const country = readAddressComponent(components, "country");
  const postalCode = readAddressComponent(components, "postal_code");
  const postalCodeSuffix = readAddressComponent(components, "postal_code_suffix");
  const addressLine1 = [streetNumber, route].filter(Boolean).join(" ").trim();

  return {
    addressLine1: addressLine1 || place?.formatted_address || "",
    city: locality || "",
    region: region || "",
    postalCode: [postalCode, postalCodeSuffix].filter(Boolean).join("-"),
    country: country || "",
  };
}

export function MyProfilePanel({ authToken, authUser, locale = "en", onProfileSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [banner, setBanner] = useState({ type: "", text: "" });
  const [subscriptionSummary, setSubscriptionSummary] = useState(null);
  const [activeStep, setActiveStep] = useState(PROFILE_STEPS[0].id);
  const [releaseLoading, setReleaseLoading] = useState(true);
  const [releaseSaving, setReleaseSaving] = useState(false);
  const [releaseConfirming, setReleaseConfirming] = useState(false);
  const [releasePlan, setReleasePlan] = useState(null);
  const [releaseDraft, setReleaseDraft] = useState(null);
  const [releaseBanner, setReleaseBanner] = useState({ type: "", text: "" });
  const [addressLookupStatus, setAddressLookupStatus] = useState(
    GOOGLE_MAPS_API_KEY ? "idle" : "unavailable"
  );
  const authUserRef = useRef(authUser);
  const addressLine1InputRef = useRef(null);
  authUserRef.current = authUser;

  const applyReleasePlan = useCallback((data) => {
    setReleasePlan(data);
    setReleaseDraft({ ...(data?.settings || {}) });
  }, []);

  const loadReleasePlan = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setReleaseLoading(true);
    setReleaseBanner({ type: "", text: "" });
    try {
      const res = await fetch(`${API_BASE}/api/release-plan`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not load the release-confidence settings.");
      }
      applyReleasePlan(data);
    } catch (e) {
      setReleaseBanner({
        type: "error",
        text:
          e.message ||
          "Could not load the release-confidence settings.",
      });
    } finally {
      setReleaseLoading(false);
    }
  }, [applyReleasePlan, authToken]);

  const loadProfile = useCallback(async () => {
    if (!authToken) {
      return;
    }
    setLoading(true);
    setBanner({ type: "", text: "" });
    try {
      const res = await fetch(`${API_BASE}/api/profile`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not load your profile.");
      }
      setForm(profileToForm(data, authUserRef.current));
      setSubscriptionSummary(data?.subscriptionSummary || null);
    } catch (e) {
      setBanner({ type: "error", text: e.message || "Could not load your profile." });
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadReleasePlan();
  }, [loadReleasePlan]);

  useEffect(() => {
    if (activeStep !== "contact" || !GOOGLE_MAPS_API_KEY || !addressLine1InputRef.current) {
      return undefined;
    }

    let cancelled = false;
    let autocomplete = null;
    setAddressLookupStatus((current) => (current === "ready" ? current : "loading"));

    loadGoogleMapsPlacesApi(GOOGLE_MAPS_API_KEY)
      .then((maps) => {
        if (cancelled || !maps?.places?.Autocomplete || !addressLine1InputRef.current) {
          return;
        }
        autocomplete = new maps.places.Autocomplete(addressLine1InputRef.current, {
          types: ["address"],
          fields: ["address_components", "formatted_address"],
        });
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          const nextAddress = parseAutocompleteAddress(place);
          setForm((prev) => ({
            ...prev,
            addressLine1: nextAddress.addressLine1 || prev.addressLine1,
            city: nextAddress.city || prev.city,
            region: nextAddress.region || prev.region,
            postalCode: nextAddress.postalCode || prev.postalCode,
            country: nextAddress.country || prev.country,
          }));
        });
        setAddressLookupStatus("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setAddressLookupStatus("error");
        }
      });

    return () => {
      cancelled = true;
      if (autocomplete && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [activeStep]);

  const derivedAge = useMemo(() => computeAgeFromIso(form.dateOfBirth), [form.dateOfBirth]);

  const setField = (key) => (event) => {
    const v = event.target.value;
    setForm((prev) => ({ ...prev, [key]: v }));
  };

  const setReleaseValue = (key) => (event) => {
    const nextValue =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setReleaseDraft((prev) => ({ ...(prev || {}), [key]: nextValue }));
  };

  const setReleaseNumber = (key) => (event) => {
    const raw = event.target.value;
    setReleaseDraft((prev) => ({
      ...(prev || {}),
      [key]: raw === "" ? "" : Number(raw),
    }));
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!authToken) {
      return;
    }
    setSaving(true);
    setBanner({ type: "", text: "" });
    const payload = {
      name: form.name,
      preferredName: form.preferredName,
      phone: form.phone,
      addressLine1: form.addressLine1,
      addressLine2: form.addressLine2,
      city: form.city,
      region: form.region,
      postalCode: form.postalCode,
      country: form.country,
      occupation: form.occupation,
      employer: form.employer,
      dateOfBirth: form.dateOfBirth,
      bloodType: form.bloodType,
      allergies: form.allergies,
      healthSummary: form.healthSummary,
      medicationsNotes: form.medicationsNotes,
      emergencyContactName: form.emergencyContactName,
      emergencyContactPhone: form.emergencyContactPhone,
    };
    try {
      const res = await fetch(`${API_BASE}/api/profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Save failed.");
      }
      setForm(profileToForm(data, authUserRef.current));
      setSubscriptionSummary(data?.subscriptionSummary || null);
      await loadReleasePlan();
      setBanner({ type: "success", text: "Profile saved." });
      if (onProfileSaved) {
        onProfileSaved(data);
      }
    } catch (e) {
      setBanner({ type: "error", text: e.message || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const saveReleasePlan = async () => {
    if (!authToken || !releaseDraft) {
      return;
    }
    setReleaseSaving(true);
    setReleaseBanner({ type: "", text: "" });
    try {
      const res = await fetch(`${API_BASE}/api/release-plan`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          enabled: Boolean(releaseDraft.enabled),
          quietPeriodDays: Number(releaseDraft.quietPeriodDays),
          reminderGraceDays: Number(releaseDraft.reminderGraceDays),
          allowTrustedContactEscalation: Boolean(releaseDraft.allowTrustedContactEscalation),
          primaryTrustedContactNodeId: releaseDraft.primaryTrustedContactNodeId || "",
          secondaryTrustedContactNodeId: releaseDraft.secondaryTrustedContactNodeId || "",
          trustedContactGraceDays: Number(releaseDraft.trustedContactGraceDays),
          highSensitivityRequiresManualReview: Boolean(
            releaseDraft.highSensitivityRequiresManualReview
          ),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not save the release-confidence settings.");
      }
      applyReleasePlan(data);
      setReleaseBanner({ type: "success", text: "Release-confidence settings saved." });
    } catch (e) {
      setReleaseBanner({
        type: "error",
        text:
          e.message ||
          "Could not save the release-confidence settings.",
      });
    } finally {
      setReleaseSaving(false);
    }
  };

  const confirmReleaseActivity = async () => {
    if (!authToken) {
      return;
    }
    setReleaseConfirming(true);
    setReleaseBanner({ type: "", text: "" });
    try {
      const res = await fetch(`${API_BASE}/api/release-plan/confirm`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || "Could not confirm your activity.");
      }
      applyReleasePlan(data);
      setReleaseBanner({ type: "success", text: "Activity confirmed. The quiet timer has been refreshed." });
    } catch (e) {
      setReleaseBanner({
        type: "error",
        text: e.message || "Could not confirm your activity.",
      });
    } finally {
      setReleaseConfirming(false);
    }
  };

  const reloadAll = async () => {
    await Promise.all([loadProfile(), loadReleasePlan()]);
  };

  const sessionSummary = useMemo(() => {
    const startAt = releasePlan?.state?.lastSessionStartedAt || "";
    const endedAt = releasePlan?.state?.lastSessionEndedAt || "";
    if (!startAt) {
      return {
        startAt: "",
        endAt: "",
        estimated: false,
        note: "We will show your most recent signed-in session here once it is recorded.",
      };
    }
    if (endedAt) {
      return {
        startAt,
        endAt: endedAt,
        estimated: false,
        note: "A sign-out time is shown when the session closed normally.",
      };
    }
    return {
      startAt,
      endAt: addMinutesToIso(startAt, 20),
      estimated: true,
      note: "No sign-out was recorded, so the end time is estimated as 20 minutes after sign-in.",
    };
  }, [releasePlan]);

  if (loading) {
    return (
      <div className="app-page app-profile-page">
        <p className="profile-page-lead" role="status">
          Loading your profile…
        </p>
      </div>
    );
  }

  const releaseStatusMeta =
    RELEASE_STATUS_COPY[releasePlan?.state?.status] || RELEASE_STATUS_COPY.disabled;
  const trustedContactOptions = releasePlan?.trustedContactOptions || [];
  const activeStepIndex = Math.max(
    PROFILE_STEPS.findIndex((step) => step.id === activeStep),
    0
  );
  const activeStepMeta = PROFILE_STEPS[activeStepIndex];
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === PROFILE_STEPS.length - 1;
  const addressLookupHint =
    addressLookupStatus === "loading"
      ? "Loading address suggestions..."
      : addressLookupStatus === "error"
        ? "Address suggestions are unavailable right now. You can still enter your address manually."
        : "Start typing to choose a suggested address when available, or enter your address manually.";
  const familyCoverage = releasePlan?.familyCoverage || {
    totalFamilyMembers: 0,
    contactableFamilyMembers: 0,
  };
  const auditEvents = releasePlan?.audit || [];
  const jumpToStep = (nextIndex) => {
    const boundedIndex = Math.min(
      PROFILE_STEPS.length - 1,
      Math.max(0, nextIndex)
    );
    setActiveStep(PROFILE_STEPS[boundedIndex].id);
  };

  return (
    <div className="app-page app-profile-page">
      <form className="profile-card-stack" onSubmit={saveProfile}>
        {banner.text ? (
          <div
            className={`profile-alert ${banner.type === "success" ? "success" : "error"}`}
            role="status"
          >
            {banner.text}
          </div>
        ) : null}

        {subscriptionSummary?.isSubscribed ? (
          <section className="profile-section profile-subscription-section" aria-labelledby="profile-subscription-heading">
            <header>
              <h2 id="profile-subscription-heading">Subscription</h2>
              <p>Your current enrolled plan, renewal schedule, and access window.</p>
            </header>
            <div className="profile-subscription-summary">
              <article className="profile-release-stat profile-release-stat-good">
                <span className="profile-release-stat-label">Current plan</span>
                <strong>{subscriptionSummary.planName}</strong>
                <p>{subscriptionSummary.checkoutLabel}</p>
              </article>
              <article className="profile-release-stat">
                <span className="profile-release-stat-label">Start date</span>
                <strong>{formatSubscriptionDate(subscriptionSummary.startDate, locale)}</strong>
                <p>Your free first-year access started on this date.</p>
              </article>
              <article className="profile-release-stat">
                <span className="profile-release-stat-label">Expiry date</span>
                <strong>{formatSubscriptionDate(subscriptionSummary.expiryDate, locale)}</strong>
                <p>Renewal begins after this access window unless you change the plan.</p>
              </article>
            </div>
          </section>
        ) : null}

        <section className="profile-stepper" aria-labelledby="profile-stepper-heading">
          <div className="profile-stepper-head">
            <div>
              <p className="profile-stepper-kicker">Profile steps</p>
              <h2 id="profile-stepper-heading">{activeStepMeta.title}</h2>
              <p>{activeStepMeta.detail}</p>
            </div>
            <div className="profile-stepper-progress" aria-live="polite">
              <strong>
                Step {activeStepIndex + 1} of {PROFILE_STEPS.length}
              </strong>
              <span>Move through your profile in smaller, calmer sections.</span>
            </div>
          </div>

          <div className="profile-stepper-tabs" role="tablist" aria-label="Profile sections">
            {PROFILE_STEPS.map((step, index) => (
              <button
                key={step.id}
                type="button"
                role="tab"
                aria-selected={activeStep === step.id}
                className={`profile-step-button${activeStep === step.id ? " is-active" : ""}${
                  index < activeStepIndex ? " is-complete" : ""
                }`}
                onClick={() => setActiveStep(step.id)}
              >
                <span className="profile-step-button-index">{index + 1}</span>
                <span className="profile-step-button-copy">
                  <strong>{step.shortLabel}</strong>
                  <small>{step.title}</small>
                </span>
              </button>
            ))}
          </div>
        </section>

        <div className="profile-step-panels">
          {activeStep === "basics" ? (
            <section className="profile-section" aria-labelledby="profile-personal-heading">
              <header>
                <h2 id="profile-personal-heading">Personal information</h2>
                <p>Name and contact details used for your account and legacy planning.</p>
              </header>
              <div className="profile-form-grid cols-2">
                <div>
                  <label className="field-label" htmlFor="profile-name">
                    Full name
                  </label>
                  <input
                    id="profile-name"
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={setField("name")}
                    required
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="profile-preferred">
                    Preferred name <span className="profile-optional">(optional)</span>
                  </label>
                  <input
                    id="profile-preferred"
                    type="text"
                    autoComplete="nickname"
                    value={form.preferredName}
                    onChange={setField("preferredName")}
                    placeholder="What we should call you"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="profile-email">
                    Email
                  </label>
                  <input
                    id="profile-email"
                    type="email"
                    value={authUser?.email || ""}
                    readOnly
                    className="profile-readonly-input"
                    aria-describedby="profile-email-hint"
                  />
                  <p id="profile-email-hint" className="profile-field-hint">
                    Sign-in email is managed with your account provider and cannot be changed here.
                  </p>
                </div>
                <div>
                  <label className="field-label" htmlFor="profile-phone">
                    Phone <span className="profile-optional">(optional)</span>
                  </label>
                  <input
                    id="profile-phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={setField("phone")}
                    placeholder="+1 …"
                  />
                </div>
              </div>
            </section>
          ) : null}

          {activeStep === "contact" ? (
            <>
              <WhatsAppLinkPanel authToken={authToken} phoneValue={form.phone} />

              <section className="profile-section" aria-labelledby="profile-address-heading">
                <header>
                  <h2 id="profile-address-heading">Address</h2>
                  <p>Helps nominees and advisors reach the right place if paper mail is ever needed.</p>
                </header>
                <div className="profile-form-grid">
                  <div>
                    <label className="field-label" htmlFor="profile-line1">
                      Street address
                    </label>
                    <input
                      id="profile-line1"
                      type="text"
                      ref={addressLine1InputRef}
                      autoComplete="address-line1"
                      value={form.addressLine1}
                      onChange={setField("addressLine1")}
                    />
                    <p className="profile-field-hint">{addressLookupHint}</p>
                  </div>
                  <div>
                    <label className="field-label" htmlFor="profile-line2">
                      Apt / suite <span className="profile-optional">(optional)</span>
                    </label>
                    <input
                      id="profile-line2"
                      type="text"
                      autoComplete="address-line2"
                      value={form.addressLine2}
                      onChange={setField("addressLine2")}
                    />
                  </div>
                  <div className="profile-form-grid cols-2">
                    <div>
                      <label className="field-label" htmlFor="profile-city">
                        City
                      </label>
                      <input
                        id="profile-city"
                        type="text"
                        autoComplete="address-level2"
                        value={form.city}
                        onChange={setField("city")}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="profile-region">
                        Province / territory
                      </label>
                      <input
                        id="profile-region"
                        type="text"
                        autoComplete="address-level1"
                        value={form.region}
                        onChange={setField("region")}
                        placeholder="e.g. Ontario"
                      />
                    </div>
                  </div>
                  <div className="profile-form-grid cols-2">
                    <div>
                      <label className="field-label" htmlFor="profile-postal">
                        Postal code
                      </label>
                      <input
                        id="profile-postal"
                        type="text"
                        autoComplete="postal-code"
                        value={form.postalCode}
                        onChange={setField("postalCode")}
                        placeholder="A1A 1A1"
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="profile-country">
                        Country
                      </label>
                      <input
                        id="profile-country"
                        type="text"
                        autoComplete="country-name"
                        value={form.country}
                        onChange={setField("country")}
                      />
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : null}

          {activeStep === "context" ? (
            <section className="profile-section" aria-labelledby="profile-work-heading">
              <header>
                <h2 id="profile-work-heading">Occupation</h2>
                <p>Optional context for your records and any future verification.</p>
              </header>
              <div className="profile-form-grid cols-2">
                <div>
                  <label className="field-label" htmlFor="profile-occupation">
                    Role / title
                  </label>
                  <input
                    id="profile-occupation"
                    type="text"
                    value={form.occupation}
                    onChange={setField("occupation")}
                    placeholder="e.g. Teacher, Engineer"
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="profile-employer">
                    Employer / organization <span className="profile-optional">(optional)</span>
                  </label>
                  <input
                    id="profile-employer"
                    type="text"
                    value={form.employer}
                    onChange={setField("employer")}
                  />
                </div>
              </div>
            </section>
          ) : null}

          {activeStep === "health" ? (
            <section className="profile-section" aria-labelledby="profile-health-heading">
              <header>
                <h2 id="profile-health-heading">Age &amp; health</h2>
                <p>
                  Stored securely with the rest of your profile. You can leave any field blank;
                  only share what you are comfortable including for your legacy file.
                </p>
              </header>
              <div className="profile-form-grid cols-2">
                <div>
                  <label className="field-label" htmlFor="profile-dob">
                    Date of birth <span className="profile-optional">(optional)</span>
                  </label>
                  <input
                    id="profile-dob"
                    type="date"
                    value={form.dateOfBirth}
                    onChange={setField("dateOfBirth")}
                  />
                  {derivedAge != null ? (
                    <p className="profile-age-chip" aria-live="polite">
                      Age today: {derivedAge}
                    </p>
                  ) : null}
                </div>
                <div>
                  <label className="field-label" htmlFor="profile-blood">
                    Blood type <span className="profile-optional">(optional)</span>
                  </label>
                  <select
                    id="profile-blood"
                    value={form.bloodType}
                    onChange={setField("bloodType")}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                    <option value="Unknown">Unknown</option>
                  </select>
                </div>
              </div>
              <div className="profile-form-grid">
                <div>
                  <label className="field-label" htmlFor="profile-allergies">
                    Allergies <span className="profile-optional">(optional)</span>
                  </label>
                  <textarea
                    id="profile-allergies"
                    rows={3}
                    value={form.allergies}
                    onChange={setField("allergies")}
                    placeholder="Medications, foods, environmental — whatever should be known in an emergency."
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="profile-meds">
                    Medications <span className="profile-optional">(optional)</span>
                  </label>
                  <textarea
                    id="profile-meds"
                    rows={3}
                    value={form.medicationsNotes}
                    onChange={setField("medicationsNotes")}
                    placeholder="Current medications or instructions you want on file."
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="profile-health-summary">
                    Health summary <span className="profile-optional">(optional)</span>
                  </label>
                  <textarea
                    id="profile-health-summary"
                    rows={5}
                    value={form.healthSummary}
                    onChange={setField("healthSummary")}
                    placeholder="Conditions, care team notes, or anything trusted people should know later."
                  />
                </div>
                <div className="profile-form-grid cols-2">
                  <div>
                    <label className="field-label" htmlFor="profile-ec-name">
                      Emergency contact name <span className="profile-optional">(optional)</span>
                    </label>
                    <input
                      id="profile-ec-name"
                      type="text"
                      value={form.emergencyContactName}
                      onChange={setField("emergencyContactName")}
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="profile-ec-phone">
                      Emergency contact phone <span className="profile-optional">(optional)</span>
                    </label>
                    <input
                      id="profile-ec-phone"
                      type="tel"
                      value={form.emergencyContactPhone}
                      onChange={setField("emergencyContactPhone")}
                    />
                  </div>
                </div>
              </div>
              <p className="profile-privacy-note">
                This information is not medical advice and is not shared automatically with
                emergency services. It stays inside your private AfterMe profile for your chosen
                release path.
              </p>
            </section>
          ) : null}

          {activeStep === "release" ? (
            <section
              className="profile-section profile-release-section"
              aria-labelledby="profile-release-heading"
            >
              <header>
                <h2 id="profile-release-heading">Release confidence</h2>
                <p>
                  Configure a quiet inactivity check-in, optional trusted-contact follow-up, and a
                  manual-review backstop for sensitive situations.
                </p>
              </header>

              <p className="profile-step-inline-note">
                Profile details save separately from release settings, so you can adjust this flow
                without touching the rest of your account.
              </p>

              {releaseBanner.text ? (
                <div
                  className={`profile-alert ${
                    releaseBanner.type === "success" ? "success" : "error"
                  }`}
                  role="status"
                >
                  {releaseBanner.text}
                </div>
              ) : null}

              {releaseLoading ? (
                <p className="profile-page-lead" role="status">
                  Loading release-confidence settings…
                </p>
              ) : releaseDraft ? (
                <>
                  <div className="profile-release-summary">
                    <article
                      className={`profile-release-stat profile-release-stat-${releaseStatusMeta.tone}`}
                    >
                      <span className="profile-release-stat-label">Current stage</span>
                      <strong>{releaseStatusMeta.label}</strong>
                      <p>{releaseStatusMeta.detail}</p>
                    </article>
                    <article className="profile-release-stat">
                      <span className="profile-release-stat-label">Last activity</span>
                      <strong>{formatDateTimeLabel(releasePlan?.state?.lastActivityAt, locale)}</strong>
                      <p>Any meaningful account action resets the quiet timer.</p>
                    </article>
                    <article className="profile-release-stat">
                      <span className="profile-release-stat-label">Next quiet step</span>
                      <strong>{formatDateTimeLabel(releasePlan?.state?.nextActionAt, locale)}</strong>
                      <p>
                        {releaseDraft.enabled
                          ? "If there is still no activity by then, the next staged check-in begins."
                          : "Turn monitoring on to schedule a quiet follow-up window."}
                      </p>
                    </article>
                  </div>

                  <article className="profile-release-session-card">
                    <div className="profile-release-session-head">
                      <div>
                        <span className="profile-release-stat-label">Last signed-in session</span>
                        <strong>
                          {sessionSummary.startAt
                            ? "Recent session window"
                            : "No recent session recorded"}
                        </strong>
                      </div>
                      {sessionSummary.estimated ? (
                        <span className="profile-release-session-badge">Estimated sign-out</span>
                      ) : null}
                    </div>
                    <div className="profile-release-session-grid">
                      <div className="profile-release-session-row">
                        <span>Signed in</span>
                        <strong>{formatDateTimeLabel(sessionSummary.startAt, locale)}</strong>
                      </div>
                      <div className="profile-release-session-row">
                        <span>Signed out</span>
                        <strong>{formatDateTimeLabel(sessionSummary.endAt, locale)}</strong>
                      </div>
                    </div>
                    <p className="profile-release-session-note">{sessionSummary.note}</p>
                  </article>

                  <div className="profile-form-grid cols-2 profile-release-grid">
                    <div className="profile-release-control-card">
                      <label className="profile-checkbox-row">
                        <input
                          type="checkbox"
                          checked={Boolean(releaseDraft.enabled)}
                          onChange={setReleaseValue("enabled")}
                        />
                        <span>
                          <strong>Enable quiet inactivity confirmation</strong>
                          <small>
                            Start with a private email check-in instead of anything noisy.
                          </small>
                        </span>
                      </label>
                    </div>

                    <div className="profile-release-control-card">
                      <label className="profile-checkbox-row">
                        <input
                          type="checkbox"
                          checked={Boolean(releaseDraft.highSensitivityRequiresManualReview)}
                          onChange={setReleaseValue("highSensitivityRequiresManualReview")}
                        />
                        <span>
                          <strong>Require manual review for high-sensitivity material</strong>
                          <small>
                            Use a human checkpoint before sensitive items move any further.
                          </small>
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="field-label" htmlFor="release-quiet-days">
                        Quiet period before first check-in
                      </label>
                      <input
                        id="release-quiet-days"
                        type="number"
                        min="1"
                        max="365"
                        value={releaseDraft.quietPeriodDays ?? ""}
                        onChange={setReleaseNumber("quietPeriodDays")}
                      />
                      <p className="profile-field-hint">
                        Recommended: 90 days or more so the workflow stays quiet and low-noise.
                      </p>
                    </div>

                    <div>
                      <label className="field-label" htmlFor="release-reminder-days">
                        Reminder grace period
                      </label>
                      <input
                        id="release-reminder-days"
                        type="number"
                        min="0"
                        max="45"
                        value={releaseDraft.reminderGraceDays ?? ""}
                        onChange={setReleaseNumber("reminderGraceDays")}
                        disabled={!releaseDraft.enabled}
                      />
                      <p className="profile-field-hint">
                        Extra time between the first quiet email and a second reminder.
                      </p>
                    </div>

                    <div className="profile-release-control-card">
                      <label className="profile-checkbox-row">
                        <input
                          type="checkbox"
                          checked={Boolean(releaseDraft.allowTrustedContactEscalation)}
                          onChange={setReleaseValue("allowTrustedContactEscalation")}
                          disabled={!releaseDraft.enabled}
                        />
                        <span>
                          <strong>Allow trusted-contact follow-up</strong>
                          <small>
                            Only after quiet emails go unanswered should a trusted person be asked
                            to check in.
                          </small>
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="field-label" htmlFor="release-trusted-grace">
                        Trusted-contact grace period
                      </label>
                      <input
                        id="release-trusted-grace"
                        type="number"
                        min="0"
                        max="45"
                        value={releaseDraft.trustedContactGraceDays ?? ""}
                        onChange={setReleaseNumber("trustedContactGraceDays")}
                        disabled={
                          !releaseDraft.enabled || !releaseDraft.allowTrustedContactEscalation
                        }
                      />
                      <p className="profile-field-hint">
                        How long to wait after contacting a trusted person before manual review.
                      </p>
                    </div>

                    <div>
                      <label className="field-label" htmlFor="release-primary-contact">
                        Primary trusted contact
                      </label>
                      <select
                        id="release-primary-contact"
                        value={releaseDraft.primaryTrustedContactNodeId || ""}
                        onChange={setReleaseValue("primaryTrustedContactNodeId")}
                        disabled={
                          !releaseDraft.enabled || !releaseDraft.allowTrustedContactEscalation
                        }
                      >
                        <option value="">Select a family member with email</option>
                        {trustedContactOptions.map((option) => (
                          <option key={option.nodeId} value={option.nodeId}>
                            {buildTrustedContactLabel(option)}
                          </option>
                        ))}
                      </select>
                      <p className="profile-field-hint">
                        {familyCoverage.contactableFamilyMembers > 0
                          ? `${familyCoverage.contactableFamilyMembers} of ${familyCoverage.totalFamilyMembers} family members are reachable by email.`
                          : "Add email addresses to family members before using trusted-contact follow-up."}
                      </p>
                    </div>

                    <div>
                      <label className="field-label" htmlFor="release-secondary-contact">
                        Secondary trusted contact{" "}
                        <span className="profile-optional">(optional)</span>
                      </label>
                      <select
                        id="release-secondary-contact"
                        value={releaseDraft.secondaryTrustedContactNodeId || ""}
                        onChange={setReleaseValue("secondaryTrustedContactNodeId")}
                        disabled={
                          !releaseDraft.enabled || !releaseDraft.allowTrustedContactEscalation
                        }
                      >
                        <option value="">No backup contact</option>
                        {trustedContactOptions.map((option) => (
                          <option key={option.nodeId} value={option.nodeId}>
                            {buildTrustedContactLabel(option)}
                          </option>
                        ))}
                      </select>
                      <p className="profile-field-hint">
                        Keep this different from the primary contact so follow-up stays resilient.
                      </p>
                    </div>
                  </div>

                  <div className="profile-release-actions">
                    <button
                      type="button"
                      className="profile-save-btn"
                      onClick={saveReleasePlan}
                      disabled={releaseSaving}
                    >
                      {releaseSaving ? "Saving…" : "Save release settings"}
                    </button>
                    <button
                      type="button"
                      className="secondary profile-discard-btn"
                      onClick={confirmReleaseActivity}
                      disabled={releaseConfirming || !releaseDraft.enabled}
                    >
                      {releaseConfirming ? "Confirming…" : "Confirm I’m active"}
                    </button>
                    <button
                      type="button"
                      className="secondary profile-discard-btn"
                      onClick={loadReleasePlan}
                      disabled={releaseSaving || releaseConfirming}
                    >
                      Reload release plan
                    </button>
                  </div>

                  <div className="profile-release-audit">
                    <div className="profile-release-audit-head">
                      <h3>Recent workflow activity</h3>
                      <p>This audit trail helps you see what the inactivity workflow last did.</p>
                    </div>
                    {auditEvents.length > 0 ? (
                      <ul className="profile-release-audit-list">
                        {auditEvents.map((eventItem) => (
                          <li key={eventItem.id} className="profile-release-audit-item">
                            <div>
                              <strong>{eventItem.summary}</strong>
                              <p>{formatDateTimeLabel(eventItem.createdAt)}</p>
                            </div>
                            <span>{eventItem.actor || "system"}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="profile-field-hint">
                        No inactivity workflow events yet. Once enabled, this section will show
                        quiet check-ins, reminders, and resets after activity.
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p className="profile-page-lead">
                  Release-confidence settings are unavailable right now.
                </p>
              )}
            </section>
          ) : null}
        </div>

        <div className="profile-actions">
          <div className="profile-actions-meta">
            <strong>
              Step {activeStepIndex + 1} of {PROFILE_STEPS.length}
            </strong>
            <span>
              {activeStep === "release"
                ? "Release settings save independently, but you can still save profile details anytime."
                : "Save your profile as you go, or continue to the next section first."}
            </span>
          </div>
          <div className="profile-actions-group">
            <button
              type="button"
              className="secondary profile-discard-btn"
              onClick={() => jumpToStep(activeStepIndex - 1)}
              disabled={isFirstStep}
            >
              Back
            </button>
            {!isLastStep ? (
              <button
                type="button"
                className="secondary profile-discard-btn"
                onClick={() => jumpToStep(activeStepIndex + 1)}
              >
                Continue
              </button>
            ) : null}
            <button type="submit" className="profile-save-btn" disabled={saving}>
              {saving ? "Saving…" : "Save profile details"}
            </button>
            <button
              type="button"
              className="secondary profile-discard-btn"
              disabled={saving}
              onClick={reloadAll}
            >
              Reload from server
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
